import { useEffect, useRef, type ReactNode } from 'react';

/**
 * A panel that opens and closes smoothly.
 *
 * HOW THE HEIGHT IS ANIMATED
 *
 * Measured, animated to that exact number, then released to `auto` so the panel
 * still reflows if anything inside it changes afterwards.
 *
 * Two approaches were tried first and are recorded here so they are not tried
 * again. A `max-height` guess either clips tall content or spends half the
 * animation easing against empty space, which is what "not smooth" usually turns
 * out to be. And the fashionable `grid-template-rows: 0fr → 1fr` trick does not
 * work when the item is `overflow: hidden` — that zeroes its automatic minimum,
 * so `1fr` resolves to nothing in an auto-height container. Measured in a
 * browser, not assumed.
 *
 * WHY THE CONTENT STAYS MOUNTED
 *
 * Collapsing from `auto` needs a real height to animate away from, so the
 * children have to be there while it closes. Being in the page while invisible
 * is a problem of its own — a screen reader would read it, and Tab would stop on
 * things nobody can see — so a closed panel is marked `inert` and hidden from
 * assistive technology. Invisible and unreachable, rather than merely invisible.
 */
export default function Expander({ open, children }: { open: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const first = useRef(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // No animation on the very first paint, or every panel on the page would
    // animate itself shut as the screen loads.
    if (first.current) {
      first.current = false;
      el.style.height = open ? 'auto' : '0px';
      // OVERFLOW RIDES WITH THE STATE. The drawer clips (overflow: hidden) only
      // while it is animating or shut, so the slide looks clean and a closed panel
      // shows nothing. Once it is settled OPEN it must NOT clip, or a tooltip, a
      // menu or any popover that a child opens inside it is cut off at the drawer's
      // edge (the Delivery-signal 'i' did exactly this). Settled-open = visible.
      el.style.overflow = open ? 'visible' : 'hidden';
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.style.height = open ? 'auto' : '0px';
      el.style.overflow = open ? 'visible' : 'hidden';
      return;
    }

    if (open) {
      // Clip while it grows, so content below the fold does not spill out before
      // its row of the height is there to hold it; released to `visible` on settle.
      el.style.overflow = 'hidden';
      el.style.height = `${el.scrollHeight}px`;
      // Once open, the panel MUST rest at `auto`, never at the measured pixel
      // height: `auto` is the only value that still fits the content if it grew
      // after measuring (a late web font, a new row), and a panel stuck at a
      // stale, short pixel height clips its own last rows. `transitionend` is the
      // normal trigger, but it does not always fire — WebKit drops it, and a
      // flex-constrained parent can finish the layout without ever transitioning
      // the height — so a timer releases to `auto` regardless. Whichever runs
      // first wins; the other is a no-op.
      let settled = false;
      const release = () => {
        if (settled) return;
        settled = true;
        if (ref.current) {
          ref.current.style.height = 'auto';
          // Settled open — stop clipping so a child popover can escape the drawer.
          ref.current.style.overflow = 'visible';
        }
      };
      const done = (e: TransitionEvent) => {
        if (e.propertyName !== 'height') return;
        release();
      };
      el.addEventListener('transitionend', done);
      // The slide is var(--ease-slow) = 240ms; release a safe margin past it.
      const timer = window.setTimeout(release, 400);
      return () => {
        el.removeEventListener('transitionend', done);
        window.clearTimeout(timer);
      };
    }

    // From `auto` there is nothing to animate away from, so the current height
    // is pinned first and the browser given a frame before it goes to zero. Clip
    // again the instant it starts closing, so nothing spills as it slides shut.
    el.style.overflow = 'hidden';
    el.style.height = `${el.scrollHeight}px`;
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (ref.current) ref.current.style.height = '0px';
      })
    );
    return () => cancelAnimationFrame(id);
  }, [open]);

  return (
    <div className="drawer" ref={ref} aria-hidden={!open} {...(!open ? { inert: '' } : {})}>
      <div className={`drawer-pad ${open ? 'is-open' : ''}`}>{children}</div>
    </div>
  );
}
